"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/apiClient";

export interface GrantSummary {
  partnerCode: string;
  partnerRole: "CUSTOMER_ADMIN" | "CUSTOMER_USER";
  partnerName: string | null;
}

export interface PortalUser {
  userId: string;
  email: string;
  displayName: string | null;
  internalRole: string | null;
  status: string;
  identitySource: string;
  grants: GrantSummary[];
  totpEnrolled: boolean | null;
  mfaEnrolmentRequired: boolean;
}

export interface CognitoChallenge {
  name: string;
  session: string;
  email: string;
}

interface AuthContextValue {
  user: PortalUser | null;
  loading: boolean;
  login: (googleToken: string) => Promise<void>;
  cognitoLogin: (email: string, password: string) => Promise<{ challenge: CognitoChallenge } | void>;
  respondToChallenge: (email: string, challengeName: string, session: string, responses: Record<string, string>) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  validateResetToken: (token: string) => Promise<{ valid: boolean; code?: string }>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";
    fetch(`${API_BASE}/customer-portal/me`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) { setUser(null); return; }
        const body = await res.json();
        setUser(body.data ?? null);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (googleToken: string) => {
    const res = await apiClient.post<{ success: boolean; user: PortalUser }>(
      "/customer-portal/auth/google-exchange",
      { token: googleToken }
    );
    setUser(res.user);
  }, []);

  const cognitoLogin = useCallback(async (email: string, password: string): Promise<{ challenge: CognitoChallenge } | void> => {
    const res = await apiClient.post<{
      success: boolean;
      user?: PortalUser;
      challenge?: CognitoChallenge;
    }>("/customer-portal/auth/login", { email, password });

    if (res.challenge) {
      return { challenge: res.challenge };
    }
    if (res.user) {
      setUser(res.user);
    }
  }, []);

  const respondToChallenge = useCallback(async (
    email: string,
    challengeName: string,
    session: string,
    responses: Record<string, string>
  ): Promise<void> => {
    const res = await apiClient.post<{
      success: boolean;
      user?: PortalUser;
      challenge?: CognitoChallenge;
    }>("/customer-portal/auth/challenge", { email, challengeName, session, responses });

    if (res.user) {
      setUser(res.user);
    }
  }, []);

  const acceptInvite = useCallback(async (token: string, password: string) => {
    await apiClient.post("/customer-portal/auth/accept-invite", { token, password });
  }, []);

  const logout = useCallback(async () => {
    await apiClient.post("/customer-portal/auth/logout").catch(() => {});
    sessionStorage.clear();
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    await apiClient.post("/customer-portal/auth/refresh");
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await apiClient.get<{ success: boolean; data: PortalUser }>("/customer-portal/me");
      if (res.data) setUser(res.data);
    } catch {
      // keep the current user if the refresh fails
    }
  }, []);

  const updateDisplayName = useCallback(async (displayName: string) => {
    const res = await apiClient.patch<{ success: boolean; data: PortalUser }>(
      "/customer-portal/me",
      { displayName }
    );
    setUser(res.data);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await apiClient.post("/customer-portal/auth/forgot-password", { email });
  }, []);

  const validateResetToken = useCallback(async (token: string): Promise<{ valid: boolean; code?: string }> => {
    const res = await apiClient.get<{ success: boolean; valid: boolean; code?: string }>(
      `/customer-portal/auth/validate-reset-token?token=${encodeURIComponent(token)}`
    );
    return { valid: res.valid, code: res.code };
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    await apiClient.post("/customer-portal/auth/reset-password", { token, password });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, cognitoLogin, respondToChallenge, acceptInvite, logout, refreshSession, refreshUser, updateDisplayName, requestPasswordReset, validateResetToken, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
